/**
 * @fileoverview ClubOS Youth Academy — Players Management
 * MSC-YYYY-XXXX player codes, batch assignment, status tracking, detail view.
 * Changes: Scholarship only discounts Admission Fee (Monthly is fixed from config),
 *          DOB rejects future dates, Enrollment tab (renamed from Fees),
 *          Plan details section removed, injury section removed from view.
 */

import { useState, useEffect, useCallback, useMemo, useRef, memo } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import { academyService } from "api";
import { useAuth } from "context/AuthContext";
import FootballLoader from "components/FootballLoader";
import { buildBrandedReport, addWatermarkToPdf } from "config/brandingHelpers";
import { TYPOGRAPHY, COLORS } from "config/branding";
import BulkUploadDialog from "./BulkUploadDialog";

const STATUS_COLOR = { Active: "success", Inactive: "secondary", Trial: "warning" };
const FEE_PLAN_COLOR = { Standard: "info", "Scholarship (25%)": "warning", "Full Waiver": "success" };
const ATTENDANCE_COLOR = { Present: "success", Late: "warning", Absent: "error", Leave: "info" };

const DEFAULT_FEE_PLANS = [
  { value: "Standard", label: "Standard", effectiveMonthlyFee: 0, effectiveAdmissionFee: 0, subsidyPct: 0 },
];

const CURRENT_YEAR = new Date().getFullYear();
const TODAY_ISO = new Date().toISOString().slice(0, 10);

const emptyForm = {
  fullName: "", dateOfBirth: "", parentName: "", parentPhone: "", alternatePhone: "",
  parentEmail: "", address: "", monthlyFee: "", feePlan: "", batchId: "", status: "Active", notes: "",
};

const ADMISSION_ITEM_TYPES = ["Admission", "Jersey", "Shorts", "Socks", "Backpack", "Cap", "Donation"];
// Kit items disabled by default — admin can manually unlock; only Admission & Donation are normally entered
const KIT_DISABLED_TYPES = ["Jersey", "Shorts", "Socks", "Backpack", "Cap"];

const makeDefaultItems = () =>
  ADMISSION_ITEM_TYPES.map((type) => ({ itemType: type, amount: "", quantity: 1, isPaid: false, notes: "" }));

const emptyAdmissionForm = {
  admissionDate: new Date().toISOString().slice(0, 10),
};

// ── Validate player form ────────────────────────────────────────────────────────
function validatePlayerForm(form, admissionItems, admissionDate) {
  const errs = {};
  if (!form.fullName.trim()) errs.fullName = "Full name is required.";
  if (!form.dateOfBirth) {
    errs.dateOfBirth = "Date of birth is required.";
  } else if (form.dateOfBirth >= TODAY_ISO) {
    errs.dateOfBirth = "Date of birth must be before today.";
  }
  if (!form.parentName.trim()) errs.parentName = "Parent/Guardian name is required.";
  if (!form.parentPhone.trim()) errs.parentPhone = "Parent phone is required.";
  else if (!/^\d{10}$/.test(form.parentPhone.replace(/\s/g, ""))) errs.parentPhone = "Enter a valid 10-digit phone number.";
  if (form.alternatePhone && !/^\d{10}$/.test(form.alternatePhone.replace(/\s/g, ""))) errs.alternatePhone = "Enter a valid 10-digit number.";
  if (form.parentEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.parentEmail)) errs.parentEmail = "Enter a valid email address.";
  if (!form.address.trim()) errs.address = "Address is required.";
  if (form.monthlyFee === "" || form.monthlyFee === undefined || form.monthlyFee === null || Number(form.monthlyFee) < 0) errs.monthlyFee = "Monthly fee is required.";
  if (!form.feePlan) errs.feePlan = "Fee plan is required.";
  if (!form.batchId) errs.batchId = "Batch assignment is required.";
  if (admissionDate !== "skip" && !admissionDate) errs.admissionDate = "Admission date is required.";
  if (admissionDate !== "skip") {
    const hasAnyAmount = admissionItems.some((it) => Number(it.amount) > 0);
    if (!hasAnyAmount) errs.admissionItems = "Please fill in at least one admission fee amount before saving.";
    const admissionItem = admissionItems.find((it) => it.itemType === "Admission");
    if (admissionItem && Number(admissionItem.amount) > 0 && !admissionItem.isPaid) {
      errs.admissionItems = "Admission payment must be marked as paid to add a new student.";
    }
  }
  return errs;
}

// ── Generate PDF report ─────────────────────────────────────────────────────────
async function downloadPlayersPDF(players, filterStatus, filterBatch, batches, yearRange, search) {
  const batchName = batches.find((b) => b.id === filterBatch)?.name ?? "All Batches";

  const { doc, autoTable, startY, headStyles, altStyles } = await buildBrandedReport(
    "Academy Players Report",
    [`Filters — Status: ${filterStatus || "All"} | Batch: ${batchName} | Search: ${search || "None"}`],
  );

  autoTable(doc, {
    startY,
    head: [["#", "Player Code", "Full Name", "DOB", "Age Grp", "Batch", "Fee/Mo", "Plan", "Admission Date", "Parent", "Phone", "Status"]],
    body: players.map((p, i) => [
      i + 1,
      p.playerCode,
      p.fullName,
      p.dateOfBirth ? String(p.dateOfBirth).slice(0, 10) : "",
      p.ageGroup,
      p.batchName ?? "Unassigned",
      `Rs.${Number(p.monthlyFee).toLocaleString("en-IN")}`,
      p.feePlan ?? "Standard",
      p.admissionDate ? String(p.admissionDate).slice(0, 10) : "—",
      p.parentName ?? "",
      p.parentPhone ?? "",
      p.status,
    ]),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { ...headStyles, fontSize: 8 },
    alternateRowStyles: altStyles,
  });

  await addWatermarkToPdf(doc);
  doc.save(`players-report-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ── Player row ─────────────────────────────────────────────────────────────────
const PlayerRow = memo(function PlayerRow({ player, onEdit, onView, onDelete, isAdmin }) {
  return (
    <SoftBox
      display="flex" alignItems="center" py={1} px={2}
      sx={{
        borderBottom: "1px solid #f5f5f5",
        cursor: "pointer",
        minWidth: 950,
        animation: "playerRowFadeIn 0.2s ease-out",
        "@keyframes playerRowFadeIn": {
          from: { opacity: 0, transform: "translateY(-4px)" },
          to:   { opacity: 1, transform: "translateY(0)" },
        },
        "&:hover": { backgroundColor: "rgba(26,115,232,0.03)" },
      }}
      onClick={() => onView(player)}
    >
      <SoftBox flex="0 0 115px">
        <SoftTypography variant="caption" fontWeight="bold" sx={{ color: COLORS.primary.hex, fontFamily: "monospace", whiteSpace: "nowrap" }}>
          {player.playerCode}
        </SoftTypography>
      </SoftBox>
      <SoftBox flex="1" display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
        <SoftTypography variant="button" fontWeight="medium" color="dark" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {player.fullName}
        </SoftTypography>
      </SoftBox>
      <SoftBox flex="0 0 100px" textAlign="left">
        <SoftTypography variant="caption" fontWeight="medium" color="dark" sx={{ whiteSpace: "nowrap" }}>
          {player.batchName ?? "Unassigned"}
        </SoftTypography>
      </SoftBox>
      <SoftBox flex="0 0 80px" textAlign="center">
        <SoftTypography variant="caption" fontWeight="medium" color="dark" sx={{ whiteSpace: "nowrap" }}>
          ₹{Number(player.monthlyFee).toLocaleString("en-IN")}
        </SoftTypography>
      </SoftBox>
      <SoftBox flex="0 0 130px" textAlign="center">
        <SoftBadge
          badgeContent={player.feePlan ?? "Standard"}
          color={FEE_PLAN_COLOR[player.feePlan] ?? "info"}
          size="xs" container
        />
      </SoftBox>
      <SoftBox flex="0 0 100px" textAlign="center">
        <SoftTypography variant="caption" fontWeight="medium" color="dark" sx={{ whiteSpace: "nowrap" }}>
          {player.admissionDate ? String(player.admissionDate).slice(0, 10) : "—"}
        </SoftTypography>
      </SoftBox>
      <SoftBox flex="0 0 80px" textAlign="center">
        <SoftBadge badgeContent={player.status} color={STATUS_COLOR[player.status] ?? "info"} size="xs" container />
      </SoftBox>
      <SoftBox flex="0 0 70px" textAlign="right" display="flex" justifyContent="flex-end">
        <SoftButton variant="text" color="primary" iconOnly size="small"
          onClick={(e) => { e.stopPropagation(); onEdit(player); }}>
          <Icon>edit</Icon>
        </SoftButton>
        {isAdmin && (
          <SoftButton variant="text" color="error" iconOnly size="small"
            onClick={(e) => { e.stopPropagation(); onDelete(player); }}>
            <Icon>delete</Icon>
          </SoftButton>
        )}
      </SoftBox>
    </SoftBox>
  );
});

PlayerRow.propTypes = {
  player: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
};

// ── Main component ─────────────────────────────────────────────────────────────
function AcademyPlayers() {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === "System Administrator";

  const [players, setPlayers]           = useState([]);
  const [batches, setBatches]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchDebounceRef = useRef(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterBatch, setFilterBatch]   = useState("");
  const [yearRange, setYearRange]       = useState([2015, CURRENT_YEAR]);
  const [sortAdmissionDate, setSortAdmissionDate] = useState("desc"); // "asc" or "desc"

  const [feePlans, setFeePlans]             = useState(DEFAULT_FEE_PLANS);
  // Config-driven base fees (fallback to 800 / 4000 if API unavailable)
  const [configMonthlyFee, setConfigMonthlyFee]     = useState(800);
  const [configAdmissionFee, setConfigAdmissionFee] = useState(4000);

  const [dialogOpen, setDialogOpen]         = useState(false);
  const [isExistingPlayer, setIsExistingPlayer] = useState(false); // true = no admission fee required
  const [editing, setEditing]               = useState(null);
  const [form, setForm]                     = useState(emptyForm);
  const [admissionDate, setAdmissionDate]   = useState(new Date().toISOString().slice(0, 10));
  const [admissionItems, setAdmissionItems] = useState(makeDefaultItems());
  const [admissionLocked, setAdmissionLocked] = useState(false);
  const [admissionPaymentMode, setAdmissionPaymentMode] = useState("Cash");
  const [admissionTransactionRef, setAdmissionTransactionRef] = useState("");
  const [saving, setSaving]                 = useState(false);
  const [fieldErrors, setFieldErrors]       = useState({});
  const [saveError, setSaveError]           = useState("");

  const [bulkOpen, setBulkOpen]           = useState(false);

  // Delete player
  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [deleteFeeRecords, setDeleteFeeRecords] = useState(false);
  const [deleting, setDeleting]           = useState(false);
  const [deleteError, setDeleteError]     = useState("");

  // Detail view
  const [viewPlayer, setViewPlayer]       = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [attendance, setAttendance]       = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      academyService.getPlayers(),
      academyService.getBatches({ status: "Active" }),
      academyService.getFeePlans().catch(() => null),
      academyService.getConfigSettings().catch(() => null),
    ])
      .then(([p, b, plans, cfg]) => {
        setPlayers(Array.isArray(p) ? p : []);
        setBatches(Array.isArray(b) ? b : []);
        if (Array.isArray(plans) && plans.length > 0) setFeePlans(plans);
        if (Array.isArray(cfg)) {
          const mf = cfg.find((x) => x.key === "MonthlyFee");
          const af = cfg.find((x) => x.key === "AdmissionFee");
          if (mf) setConfigMonthlyFee(Number(mf.value) || 800);
          if (af) setConfigAdmissionFee(Number(af.value) || 4000);
        }
      })
      .catch((err) => {
        console.error("Failed to load players:", err);
        setPlayers([]);
        setBatches([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(search), 180);
    return () => clearTimeout(searchDebounceRef.current);
  }, [search]);

  const displayedPlayers = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return players
      .filter((p) => {
        if (filterStatus && p.status !== filterStatus) return false;
        if (filterBatch && p.batchId !== filterBatch) return false;
        if (q) {
          const hay = `${p.fullName} ${p.playerCode} ${p.parentName ?? ""}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        if (p.admissionDate) {
          const y = new Date(p.admissionDate).getFullYear();
          if (y < yearRange[0] || y > yearRange[1]) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const da = a.admissionDate ? new Date(a.admissionDate).getTime() : 0;
        const db = b.admissionDate ? new Date(b.admissionDate).getTime() : 0;
        return sortAdmissionDate === "desc" ? db - da : da - db;
      });
  }, [players, debouncedSearch, filterStatus, filterBatch, yearRange, sortAdmissionDate]);

  const openAdd = () => {
    setEditing(null);
    setIsExistingPlayer(false);
    const firstPlan = feePlans[0];
    const initAdmission = firstPlan ? Math.round(firstPlan.effectiveAdmissionFee ?? firstPlan.baseAdmissionFee * (1 - firstPlan.subsidyPct / 100)) : configAdmissionFee;
    setForm({ ...emptyForm, feePlan: firstPlan?.name ?? "", monthlyFee: String(configMonthlyFee) });
    setAdmissionDate(new Date().toISOString().slice(0, 10));
    const defaults = makeDefaultItems();
    setAdmissionItems(defaults.map((it) =>
      it.itemType === "Admission" ? { ...it, amount: String(initAdmission) } : it
    ));
    setAdmissionLocked(false);
    setAdmissionPaymentMode("Cash");
    setAdmissionTransactionRef("");
    setFieldErrors({});
    setSaveError("");
    setDialogOpen(true);
  };

  // Existing player — no admission fee required, Standard plan, skip Enrollment validation
  const openAddExisting = () => {
    setEditing(null);
    setIsExistingPlayer(true);
    const standardPlan = feePlans.find((p) => p.name === "Standard") ?? feePlans[0];
    setForm({ ...emptyForm, feePlan: standardPlan?.name ?? "Standard", monthlyFee: String(configMonthlyFee) });
    setAdmissionDate(new Date().toISOString().slice(0, 10));
    setAdmissionItems(makeDefaultItems()); // all blank — not required
    setAdmissionLocked(false);
    setAdmissionPaymentMode("Cash");
    setAdmissionTransactionRef("");
    setFieldErrors({});
    setSaveError("");
    setDialogOpen(true);
  };

  const openEdit = useCallback(async (player) => {
    setEditing(player);
    setForm({
      fullName: player.fullName,
      dateOfBirth: player.dateOfBirth ? String(player.dateOfBirth).slice(0, 10) : "",
      parentName: player.parentName ?? "",
      parentPhone: player.parentPhone ?? "",
      alternatePhone: player.alternatePhone ?? "",
      parentEmail: player.parentEmail ?? "",
      address: player.address ?? "",
      monthlyFee: player.monthlyFee,
      feePlan: player.feePlan ?? (feePlans[0]?.name ?? ""),
      batchId: player.batchId ?? "",
      status: player.status ?? "Active",
      notes: player.notes ?? "",
    });
    setAdmissionDate(player.admissionDate ? String(player.admissionDate).slice(0, 10) : new Date().toISOString().slice(0, 10));
    const defaults = makeDefaultItems();
    try {
      const existing = await academyService.getAdmissionItems(player.id);
      if (Array.isArray(existing) && existing.length > 0) {
        const merged = defaults.map((d) => {
          const found = existing.find((e) => e.itemType === d.itemType);
          if (!found) return d;
          const qty = found.itemType === "Admission" ? 1 : found.quantity;
          return { itemType: found.itemType, amount: String(found.amount), quantity: qty, isPaid: found.isPaid, notes: found.notes ?? "" };
        });
        setAdmissionItems(merged);
        setAdmissionLocked(true);
      } else {
        setAdmissionItems(defaults);
        setAdmissionLocked(false);
      }
    } catch {
      setAdmissionItems(defaults);
      setAdmissionLocked(false);
    }
    setFieldErrors({});
    setSaveError("");
    setDialogOpen(true);
  }, [feePlans]);

  const openView = useCallback(async (player) => {
    setViewPlayer(player);
    setDetailLoading(true);
    setAttendance([]);
    try {
      const att = await academyService.getAttendance({ playerId: player.id });
      setAttendance(Array.isArray(att) ? att.slice(0, 10) : []);
    } catch (e) {
      console.error(e);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const f = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const updateAdmissionItem = (index, field, value) => {
    setAdmissionItems((prev) => prev.map((it, i) => i === index ? { ...it, [field]: value } : it));
    setFieldErrors((prev) => ({ ...prev, admissionItems: undefined }));
  };

  const handleFeePlanSelect = (planName) => {
    const plan = feePlans.find((p) => p.name === planName);
    const effectiveAdmission = plan ? Math.round(plan.effectiveAdmissionFee ?? plan.baseAdmissionFee * (1 - plan.subsidyPct / 100)) : configAdmissionFee;
    setForm((prev) => ({ ...prev, feePlan: planName }));
    if (!admissionLocked) {
      setAdmissionItems((prev) => prev.map((it) =>
        it.itemType === "Admission" ? { ...it, amount: String(effectiveAdmission) } : it
      ));
    }
    setFieldErrors((prev) => ({ ...prev, feePlan: undefined, monthlyFee: undefined }));
  };

  const handleSave = async () => {
    const skipAdmission = editing || isExistingPlayer;
    const errs = validatePlayerForm(form, skipAdmission ? [] : admissionItems, skipAdmission ? "skip" : admissionDate);
    if (skipAdmission) { delete errs.admissionItems; delete errs.admissionDate; }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setSaveError("Please fix the highlighted errors before saving.");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const payload = {
        ...form,
        monthlyFee: Number(form.monthlyFee) || 0,
        batchId: form.batchId || null,
        alternatePhone: form.alternatePhone || null,
        dateOfBirth: form.dateOfBirth,
        admissionDate: admissionDate || new Date().toISOString().slice(0, 10),
      };
      let playerId;
      if (editing) {
        await academyService.updatePlayer(editing.id, payload);
        playerId = editing.id;
      } else {
        playerId = await academyService.createPlayer(payload);
      }
      const itemsToSave = admissionItems
        .filter((it) => Number(it.amount) > 0)
        .map((it) => ({
          itemType: it.itemType,
          amount: Number(it.amount),
          isPaid: it.isPaid,
          quantity: Number(it.quantity) || 1,
          notes: it.notes || null,
        }));
      if (itemsToSave.length > 0) {
        await academyService.saveAdmissionItems(playerId, itemsToSave);

        // Always create an AcademyFeeRecord for admission fees on new player — enables fee tab collection
        if (!editing) {
          const totalAdmission = itemsToSave.reduce((s, it) => s + it.amount * (it.quantity || 1), 0);
          const paidItems      = itemsToSave.filter((it) => it.isPaid);
          const paidAdmission  = paidItems.reduce((s, it) => s + it.amount * (it.quantity || 1), 0);
          const admYear        = new Date(payload.admissionDate).getFullYear();
          await academyService.recordFee({
            playerId:       playerId,
            feeMonth:       0,
            feeYear:        admYear,
            totalAmount:    totalAdmission,
            paidAmount:     paidAdmission,
            discountAmount: 0,
            paymentMode:    paidAdmission > 0 ? admissionPaymentMode : "Cash",
            paymentDate:    payload.admissionDate,
            transactionRef: admissionTransactionRef || null,
            notes:          paidAdmission > 0 ? "Recorded at enrollment" : null,
            feeType:        "Admission",
          });
        }
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail ?? e?.response?.data?.title ?? e?.message ?? "Save failed. Please try again.";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const openDelete = useCallback((player) => {
    setDeleteTarget(player);
    setDeleteFeeRecords(false);
    setDeleteError("");
  }, []);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await academyService.deletePlayer(deleteTarget.id, deleteFeeRecords);
      setDeleteTarget(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail ?? e?.response?.data?.title ?? e?.message ?? "Delete failed.";
      setDeleteError(msg);
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadPDF = () => {
    downloadPlayersPDF(displayedPlayers, filterStatus, filterBatch, batches, yearRange, search);
  };

  const active   = players.filter((p) => p.status === "Active").length;
  const trial    = players.filter((p) => p.status === "Trial").length;
  const inactive = players.filter((p) => p.status === "Inactive").length;
  const thisMonth = new Date().toISOString().slice(0, 7);
  const currentMonthAdmissions = players.filter((p) =>
    p.admissionDate && String(p.admissionDate).slice(0, 7) === thisMonth
  ).length;

  // Scholarship info for display (admission only)
  const selectedPlan = feePlans.find((p) => p.name === form.feePlan);
  const scholarshipAdmission = selectedPlan && selectedPlan.subsidyPct > 0
    ? Math.round(configAdmissionFee * (1 - selectedPlan.subsidyPct / 100))
    : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" sx={{ color: TYPOGRAPHY.page.titleColor }}>Academy Players</SoftTypography>
            <SoftTypography variant="body2" sx={{ color: TYPOGRAPHY.page.subtitleColor }}>Click a player to view attendance history</SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={1} flexWrap="wrap">
            <SoftButton variant="outlined" color="primary" startIcon={<Icon>download</Icon>} onClick={handleDownloadPDF}>
              Download Report
            </SoftButton>
            <SoftButton variant="outlined" color="primary" startIcon={<Icon>upload</Icon>} onClick={() => setBulkOpen(true)}>
              Bulk Upload
            </SoftButton>
            <SoftButton variant="outlined" color="primary" startIcon={<Icon>transfer_within_a_station</Icon>} onClick={openAddExisting}>
              Add Existing Player
            </SoftButton>
            <SoftButton variant="gradient" color="primary" startIcon={<Icon>person_add</Icon>} onClick={openAdd}>
              Add New Player
            </SoftButton>
          </SoftBox>
        </SoftBox>

        {/* KPI row */}
        <SoftBox mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Total Players" }} count={String(players.length)}
                percentage={{ color: "primary", text: "registered" }} icon={{ color: "primary", component: "groups" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Active" }} count={String(active)}
                percentage={{ color: "primary", text: "training" }} icon={{ color: "primary", component: "check_circle" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "This Month Admissions" }} count={String(currentMonthAdmissions)}
                percentage={{ color: "primary", text: "new joins" }} icon={{ color: "primary", component: "person_add_alt" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Inactive" }} count={String(inactive)}
                percentage={{ color: "primary", text: "inactive" }} icon={{ color: "primary", component: "person_off" }} />
            </Grid>
          </Grid>
        </SoftBox>

        {/* Filters */}
        <Card>
          <SoftBox p={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField
              size="small" placeholder="Search by name, code, or parent…" value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Icon fontSize="small">search</Icon></InputAdornment> }}
              sx={{ minWidth: 260 }}
            />
            <TextField select size="small" label="Status" value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="">◈ All Status</MenuItem>
              <MenuItem value="Active">▲ Active</MenuItem>
              <MenuItem value="Trial">▶ Trial</MenuItem>
              <MenuItem value="Inactive">▼ Inactive</MenuItem>
            </TextField>
            <TextField select size="small" label="Batch" value={filterBatch}
              onChange={(e) => setFilterBatch(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value="">All Batches</MenuItem>
              {batches.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
            </TextField>

            <TextField select size="small" label="Year From" value={yearRange[0]}
              onChange={(e) => setYearRange([Number(e.target.value), yearRange[1]])} sx={{ minWidth: 110 }}>
              {Array.from({ length: CURRENT_YEAR - 2014 }, (_, i) => 2015 + i).map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Year To" value={yearRange[1]}
              onChange={(e) => setYearRange([yearRange[0], Number(e.target.value)])} sx={{ minWidth: 110 }}>
              {Array.from({ length: CURRENT_YEAR - 2014 }, (_, i) => 2015 + i).map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>

            {(filterStatus || filterBatch || search || yearRange[0] !== 2015 || yearRange[1] !== CURRENT_YEAR) && (
              <Chip
                label="Clear filters"
                size="small"
                onDelete={() => { setFilterStatus(""); setFilterBatch(""); setSearch(""); setDebouncedSearch(""); setYearRange([2015, CURRENT_YEAR]); }}
                sx={{ height: 28 }}
              />
            )}
            <SoftTypography variant="caption" color="text" ml="auto">
              {displayedPlayers.length} of {players.length} players
            </SoftTypography>
          </SoftBox>

          <SoftBox sx={{ overflowX: "auto" }}>
            <SoftBox display="flex" alignItems="center" px={2} py={1}
              sx={{ borderBottom: "2px solid #f0f0f0", backgroundColor: "#fafafa", minWidth: 950 }}>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 115px" }}>ID CODE</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: 1 }}>PLAYER</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 100px", textAlign: "left" }}>BATCH</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px", textAlign: "center" }}>FEE/MO</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 130px", textAlign: "center" }}>PLAN</SoftTypography>
              <SoftTypography
                variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 100px", textAlign: "center", cursor: "pointer", userSelect: "none" }}
                onClick={() => setSortAdmissionDate(sortAdmissionDate === "desc" ? "asc" : "desc")}
              >
                ADMISSION {sortAdmissionDate === "desc" ? "↓" : "↑"}
              </SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px", textAlign: "center" }}>STATUS</SoftTypography>
              <SoftBox sx={{ flex: "0 0 40px" }} />
            </SoftBox>

            {loading ? (
              <FootballLoader label="Loading players…" />
            ) : displayedPlayers.length === 0 ? (
              <SoftBox p={4} textAlign="center">
                <SoftTypography variant="caption" color="text">No players found</SoftTypography>
              </SoftBox>
            ) : (
              displayedPlayers.map((p) => (
                <PlayerRow key={p.id} player={p} onEdit={openEdit} onView={openView} onDelete={openDelete} isAdmin={isAdmin} />
              ))
            )}
          </SoftBox>
        </Card>

      </SoftBox>
      <Footer company={{ href: "#", name: "Mohammedan SC" }} links={[]} />

      {/* ── Bulk Upload dialog ── */}
      <BulkUploadDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        batches={batches}
        feePlans={feePlans}
        onUploaded={load}
      />

      {/* ── Add / Edit dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { maxHeight: "90vh" } }}>
        <DialogTitle>
          {editing ? "Edit Player" : isExistingPlayer ? "Add Existing Player" : "Add New Player"}
          {isExistingPlayer && !editing && (
            <SoftTypography variant="caption" color="text" display="block">
              Standard monthly fee — no admission charge
            </SoftTypography>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflowY: "auto" }}>
          {saveError && <Alert severity="error" sx={{ mx: 3, mt: 2 }}>{saveError}</Alert>}

          <Grid container spacing={2} sx={{ p: 3, pt: 2 }}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Full Name" value={form.fullName}
                onChange={f("fullName")} size="small"
                error={Boolean(fieldErrors.fullName)} helperText={fieldErrors.fullName} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required label="Date of Birth" type="date" value={form.dateOfBirth}
                onChange={f("dateOfBirth")} size="small" InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date(Date.now() - 86400000).toISOString().slice(0, 10) }}
                error={Boolean(fieldErrors.dateOfBirth)} helperText={fieldErrors.dateOfBirth} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth required label="Fee Plan" value={form.feePlan}
                onChange={(e) => handleFeePlanSelect(e.target.value)} size="small"
                error={Boolean(fieldErrors.feePlan)} helperText={fieldErrors.feePlan}>
                {feePlans.map((p) => (
                  <MenuItem key={p.id ?? p.name} value={p.name}>
                    {p.name}
                    {p.subsidyPct > 0 ? ` (${p.subsidyPct}% off admission)` : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {scholarshipAdmission !== null && (
              <Grid item xs={12}>
                <SoftBox sx={{ backgroundColor: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 2, px: 2, py: 1 }}>
                  <SoftTypography variant="caption" color="success" fontWeight="bold" display="block">
                    Scholarship applied — Admission fee: ₹{configAdmissionFee.toLocaleString("en-IN")} → ₹{scholarshipAdmission.toLocaleString("en-IN")}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text">
                    Monthly fee remains standard: ₹{configMonthlyFee.toLocaleString("en-IN")} / month
                  </SoftTypography>
                </SoftBox>
              </Grid>
            )}

            <Grid item xs={6}>
              <TextField fullWidth required label="Monthly Fee (₹)" type="number" value={form.monthlyFee}
                onChange={f("monthlyFee")} size="small"
                error={Boolean(fieldErrors.monthlyFee)} helperText={fieldErrors.monthlyFee} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth required label="Batch" value={form.batchId}
                onChange={f("batchId")} size="small"
                error={Boolean(fieldErrors.batchId)} helperText={fieldErrors.batchId}>
                <MenuItem value="">Unassigned</MenuItem>
                {batches.map((b) => <MenuItem key={b.id} value={b.id}>{b.name} ({b.ageGroup})</MenuItem>)}
              </TextField>
            </Grid>
            {editing && (
              <Grid item xs={6}>
                <TextField select fullWidth label="Status" value={form.status}
                  onChange={f("status")} size="small">
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Trial">Trial</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </TextField>
              </Grid>
            )}
            <Grid item xs={editing ? 6 : 12}>
              <TextField fullWidth required label="Parent / Guardian Name" value={form.parentName}
                onChange={f("parentName")} size="small"
                error={Boolean(fieldErrors.parentName)} helperText={fieldErrors.parentName} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required label="Parent Phone" value={form.parentPhone}
                onChange={f("parentPhone")} size="small"
                error={Boolean(fieldErrors.parentPhone)} helperText={fieldErrors.parentPhone} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Alternate Phone" value={form.alternatePhone}
                onChange={f("alternatePhone")} size="small"
                error={Boolean(fieldErrors.alternatePhone)} helperText={fieldErrors.alternatePhone} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Parent Email" value={form.parentEmail}
                onChange={f("parentEmail")} size="small"
                error={Boolean(fieldErrors.parentEmail)} helperText={fieldErrors.parentEmail} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required label="Address" value={form.address}
                onChange={f("address")} size="small" multiline rows={2}
                error={Boolean(fieldErrors.address)} helperText={fieldErrors.address} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" value={form.notes}
                onChange={f("notes")} size="small" multiline rows={2} />
            </Grid>

            {/* ── Admission Fees at Enrollment ── */}
            {!isExistingPlayer && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <SoftTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                    ADMISSION FEES AT ENROLLMENT
                  </SoftTypography>
                  <Alert severity="info" sx={{ py: 0.5, mb: 1.5 }}>
                    <SoftTypography variant="caption">
                      Monthly fees are payable from the <strong>Transactions tab</strong> only.
                    </SoftTypography>
                  </Alert>
                  {fieldErrors.admissionItems && (
                    <Alert severity="error" sx={{ mb: 1 }}>{fieldErrors.admissionItems}</Alert>
                  )}
                </Grid>

                <Grid item xs={4}>
                  <TextField
                    label="Admission Date" type="date" size="small" fullWidth
                    value={admissionDate}
                    onChange={(e) => { setAdmissionDate(e.target.value); setFieldErrors((p) => ({ ...p, admissionDate: undefined })); }}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldErrors.admissionDate)}
                    helperText={fieldErrors.admissionDate}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    select label="Payment Mode" size="small" fullWidth
                    value={admissionPaymentMode}
                    onChange={(e) => setAdmissionPaymentMode(e.target.value)}>
                    {["Cash", "UPI", "Online", "Cheque", "NEFT"].map((m) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Txn Ref" size="small" fullWidth
                    value={admissionTransactionRef}
                    onChange={(e) => setAdmissionTransactionRef(e.target.value)}
                    placeholder="Optional"
                  />
                </Grid>

                {admissionLocked && (
                  <Grid item xs={12}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      {isAdmin ? (
                        <SoftButton size="small" color="warning" variant="outlined"
                          onClick={() => setAdmissionLocked(false)}>
                          <Icon fontSize="small" sx={{ mr: 0.5 }}>lock_open</Icon> Unfreeze Fees
                        </SoftButton>
                      ) : (
                        <SoftTypography variant="caption" color="text" sx={{ fontStyle: "italic" }}>
                          Enrollment fees locked — contact admin to edit
                        </SoftTypography>
                      )}
                    </SoftBox>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <SoftBox display="flex" alignItems="center" px={1} py={0.5}
                    sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                    <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 110px" }}>ITEM</SoftTypography>
                    <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: 1 }}>AMOUNT (₹)</SoftTypography>
                    <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 70px", textAlign: "center" }}>QTY</SoftTypography>
                    <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 60px", textAlign: "center" }}>PAID</SoftTypography>
                  </SoftBox>
                  {admissionItems.map((item, idx) => {
                    const isKitItem = KIT_DISABLED_TYPES.includes(item.itemType);
                    const isAdmission = item.itemType === "Admission";
                    const isDisabled = admissionLocked || isKitItem;
                    return (
                      <SoftBox key={item.itemType} display="flex" alignItems="center" gap={1} px={1} py={0.75}
                        sx={{ borderBottom: "1px solid #f5f5f5", opacity: isDisabled ? 0.45 : 1 }}>
                        <SoftBox flex="0 0 110px" display="flex" alignItems="center" gap={0.5}>
                          <SoftTypography variant="caption" fontWeight="medium" color="dark">{item.itemType}</SoftTypography>
                          {isKitItem && (
                            <Tooltip title="Collect kits via Transactions tab"><Icon sx={{ fontSize: "0.75rem", color: "text.secondary" }}>lock</Icon></Tooltip>
                          )}
                        </SoftBox>
                        <TextField
                          type="number" size="small" placeholder="0"
                          value={item.amount}
                          onChange={(e) => updateAdmissionItem(idx, "amount", e.target.value)}
                          disabled={isDisabled}
                          sx={{ flex: 1 }}
                          inputProps={{ min: 0, style: { textAlign: "right" } }}
                        />
                        <TextField
                          type="number" size="small"
                          value={item.quantity}
                          onChange={(e) => updateAdmissionItem(idx, "quantity", Math.max(1, Number(e.target.value)))}
                          disabled={isDisabled || isAdmission}
                          sx={{ flex: "0 0 70px" }}
                          inputProps={{ min: 1, max: isAdmission ? 1 : undefined }}
                        />
                        <SoftBox flex="0 0 60px" textAlign="center">
                          <input
                            type="checkbox"
                            checked={item.isPaid}
                            disabled={isDisabled}
                            onChange={(e) => updateAdmissionItem(idx, "isPaid", e.target.checked)}
                            style={{ width: 18, height: 18, cursor: isDisabled ? "not-allowed" : "pointer" }}
                          />
                        </SoftBox>
                      </SoftBox>
                    );
                  })}
                  <SoftBox display="flex" justifyContent="flex-end" pt={0.5}>
                    <SoftTypography variant="caption" color="text">
                      Total: ₹{admissionItems.reduce((s, it) => s + (Number(it.amount) || 0) * (Number(it.quantity) || 1), 0).toLocaleString("en-IN")}
                      &nbsp;·&nbsp;
                      Paid: ₹{admissionItems.filter((it) => it.isPaid).reduce((s, it) => s + (Number(it.amount) || 0) * (Number(it.quantity) || 1), 0).toLocaleString("en-IN")}
                    </SoftTypography>
                  </SoftBox>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={() => setDialogOpen(false)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : editing ? "Update" : "Add New Player"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* ── Delete player confirmation dialog ── */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        {deleteTarget && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <SoftBox display="flex" alignItems="center" gap={1}>
                <Icon sx={{ color: "#d32f2f", fontSize: "1.4rem" }}>warning</Icon>
                <SoftTypography variant="h6" fontWeight="bold" color="error">Delete Player</SoftTypography>
              </SoftBox>
            </DialogTitle>
            <DialogContent>
              {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
              <Alert severity="error" sx={{ mb: 2 }}>
                <SoftTypography variant="caption" fontWeight="bold">This action is permanent and will be marked as CRITICAL in the audit history.</SoftTypography>
              </Alert>
              <SoftTypography variant="body2" color="dark" mb={2}>
                You are about to permanently delete <strong>{deleteTarget.fullName}</strong> ({deleteTarget.playerCode}).
                Attendance, injuries, and training records will also be deleted.
              </SoftTypography>
              <SoftBox display="flex" alignItems="center" gap={1} sx={{ cursor: "pointer" }}
                onClick={() => setDeleteFeeRecords((v) => !v)}>
                <input
                  type="checkbox"
                  checked={deleteFeeRecords}
                  onChange={(e) => setDeleteFeeRecords(e.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
                <SoftTypography variant="caption" fontWeight="medium" color="error">
                  Also delete all fee transactions for this player
                </SoftTypography>
              </SoftBox>
              {!deleteFeeRecords && (
                <SoftTypography variant="caption" color="text" display="block" mt={0.5} sx={{ fontStyle: "italic" }}>
                  Fee records will be retained for accounting purposes.
                </SoftTypography>
              )}
            </DialogContent>
            <DialogActions>
              <SoftButton variant="text" color="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </SoftButton>
              <SoftButton variant="gradient" color="error" onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? "Deleting…" : "Delete Player"}
              </SoftButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Player profile dialog ── */}
      <Dialog open={Boolean(viewPlayer)} onClose={() => setViewPlayer(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: "90vh" } }}>
        {viewPlayer && (
          <>
            {/* Gradient header */}
            <SoftBox
              sx={{
                background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)",
                px: 3, py: 3, color: "#fff", position: "relative",
              }}
            >
              <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start">
                <SoftBox>
                  {/* Avatar circle */}
                  <SoftBox
                    sx={{
                      width: 60, height: 60, borderRadius: "50%",
                      backgroundColor: "rgba(255,255,255,0.2)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      mb: 1.5, border: "2px solid rgba(255,255,255,0.4)",
                    }}
                  >
                    <Icon sx={{ fontSize: "2rem !important", color: "#fff" }}>person</Icon>
                  </SoftBox>
                  <SoftTypography variant="h4" fontWeight="bold" sx={{ color: "#fff", lineHeight: 1.2 }}>
                    {viewPlayer.fullName}
                  </SoftTypography>
                  <SoftTypography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", fontFamily: "monospace", mt: 0.25 }}>
                    {viewPlayer.playerCode}
                  </SoftTypography>
                  <SoftBox display="flex" gap={1} mt={1} flexWrap="wrap">
                    <SoftBadge
                      badgeContent={viewPlayer.status}
                      color={{ Active: "success", Inactive: "secondary", Trial: "warning" }[viewPlayer.status] ?? "info"}
                      size="xs" container
                    />
                    {viewPlayer.batchName && (
                      <Chip
                        label={viewPlayer.batchName}
                        size="small"
                        sx={{ backgroundColor: "rgba(255,255,255,0.2)", color: "#fff", fontSize: "0.65rem", height: 20 }}
                      />
                    )}
                    {viewPlayer.ageGroup && (
                      <Chip
                        label={viewPlayer.ageGroup}
                        size="small"
                        sx={{ backgroundColor: "rgba(255,255,255,0.15)", color: "#fff", fontSize: "0.65rem", height: 20 }}
                      />
                    )}
                  </SoftBox>
                </SoftBox>
                <SoftBox display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                  <Tooltip title="Edit Player">
                    <SoftButton
                      variant="outlined"
                      size="small"
                      startIcon={<Icon>edit</Icon>}
                      sx={{
                        color: "#fff", borderColor: "rgba(255,255,255,0.5)",
                        "&:hover": { borderColor: "#fff", backgroundColor: "rgba(255,255,255,0.1)" },
                      }}
                      onClick={() => { setViewPlayer(null); openEdit(viewPlayer); }}
                    >
                      Edit
                    </SoftButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => setViewPlayer(null)} sx={{ color: "rgba(255,255,255,0.7)" }}>
                    <Icon>close</Icon>
                  </IconButton>
                </SoftBox>
              </SoftBox>

              {/* Key stats strip */}
              <SoftBox
                display="flex"
                gap={0}
                mt={2}
                sx={{
                  backgroundColor: "rgba(255,255,255,0.1)",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                {[
                  { label: "Monthly Fee", value: `₹${Number(viewPlayer.monthlyFee).toLocaleString("en-IN")}` },
                  { label: "Fee Plan", value: viewPlayer.feePlan ?? "Standard" },
                  { label: "Admission", value: viewPlayer.admissionDate ? String(viewPlayer.admissionDate).slice(0, 10) : "—" },
                ].map((stat, i) => (
                  <SoftBox
                    key={stat.label}
                    flex={1}
                    textAlign="center"
                    py={1.5}
                    sx={{ borderRight: i < 2 ? "1px solid rgba(255,255,255,0.15)" : "none" }}
                  >
                    <SoftTypography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", display: "block" }}>
                      {stat.label}
                    </SoftTypography>
                    <SoftTypography variant="body2" fontWeight="bold" sx={{ color: "#fff" }}>
                      {stat.value}
                    </SoftTypography>
                  </SoftBox>
                ))}
              </SoftBox>
            </SoftBox>

            <DialogContent dividers sx={{ p: 0 }}>
              {detailLoading ? (
                <SoftBox py={4} display="flex" justifyContent="center" alignItems="center" gap={1}>
                  <Icon sx={{ animation: "spin 1s linear infinite", "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } } }}>
                    sports_soccer
                  </Icon>
                  <SoftTypography variant="caption" color="text">Loading profile…</SoftTypography>
                </SoftBox>
              ) : (
                <Grid container sx={{ minHeight: 0 }}>
                  {/* Left: personal info */}
                  <Grid item xs={12} md={6} sx={{ borderRight: { md: "1px solid #f0f0f0" } }}>
                    <SoftBox px={3} py={2}>
                      <SoftBox display="flex" alignItems="center" gap={1} mb={1.5}>
                        <Icon sx={{ color: "#1a73e8", fontSize: "1.1rem" }}>person</Icon>
                        <SoftTypography variant="overline" fontWeight="bold" color="text">Personal Info</SoftTypography>
                      </SoftBox>
                      {[
                        { label: "Date of Birth", value: viewPlayer.dateOfBirth ? String(viewPlayer.dateOfBirth).slice(0, 10) : "—" },
                        { label: "Age Group", value: viewPlayer.ageGroup ?? "—" },
                        { label: "Address", value: viewPlayer.address ?? "—" },
                        { label: "Notes", value: viewPlayer.notes ?? "—" },
                      ].map(({ label, value }) => (
                        <SoftBox key={label} mb={1.5}>
                          <SoftTypography variant="caption" color="text" display="block">{label}</SoftTypography>
                          <SoftTypography variant="body2" fontWeight="medium" color="dark">{value}</SoftTypography>
                        </SoftBox>
                      ))}
                    </SoftBox>

                    <Divider />
                    <SoftBox px={3} py={2}>
                      <SoftBox display="flex" alignItems="center" gap={1} mb={1.5}>
                        <Icon sx={{ color: "#1a73e8", fontSize: "1.1rem" }}>family_restroom</Icon>
                        <SoftTypography variant="overline" fontWeight="bold" color="text">Parent / Guardian</SoftTypography>
                      </SoftBox>
                      {[
                        { label: "Name", value: viewPlayer.parentName ?? "—" },
                        { label: "Phone", value: viewPlayer.parentPhone ?? "—" },
                        { label: "Alternate Phone", value: viewPlayer.alternatePhone ?? "—" },
                        { label: "Email", value: viewPlayer.parentEmail ?? "—" },
                      ].map(({ label, value }) => (
                        <SoftBox key={label} mb={1.5}>
                          <SoftTypography variant="caption" color="text" display="block">{label}</SoftTypography>
                          <SoftTypography variant="body2" fontWeight="medium" color="dark">{value}</SoftTypography>
                        </SoftBox>
                      ))}
                    </SoftBox>
                  </Grid>

                  {/* Right: attendance */}
                  <Grid item xs={12} md={6}>
                    <SoftBox px={3} py={2}>
                      <SoftBox display="flex" alignItems="center" gap={1} mb={1.5}>
                        <Icon sx={{ color: "#1a73e8", fontSize: "1.1rem" }}>fact_check</Icon>
                        <SoftTypography variant="overline" fontWeight="bold" color="text">Recent Attendance (last 10)</SoftTypography>
                      </SoftBox>
                      {attendance.length === 0 ? (
                        <SoftBox
                          sx={{ backgroundColor: "#f9f9f9", borderRadius: 2, p: 2, textAlign: "center" }}
                        >
                          <Icon sx={{ color: "#ccc", fontSize: "2rem" }}>event_busy</Icon>
                          <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                            No attendance records
                          </SoftTypography>
                        </SoftBox>
                      ) : (
                        attendance.map((a, i) => (
                          <SoftBox key={a.id} display="flex" justifyContent="space-between" alignItems="center"
                            py={0.75}
                            sx={{ borderBottom: i < attendance.length - 1 ? "1px solid #f5f5f5" : "none" }}>
                            <SoftBox>
                              <SoftTypography variant="caption" fontWeight="medium" color="dark" display="block">
                                {String(a.attendanceDate).slice(0, 10)}
                              </SoftTypography>
                              <SoftTypography variant="caption" color="text">{a.batchName}</SoftTypography>
                            </SoftBox>
                            <SoftBadge badgeContent={a.attendanceStatus} color={ATTENDANCE_COLOR[a.attendanceStatus] ?? "info"} size="xs" container />
                          </SoftBox>
                        ))
                      )}
                    </SoftBox>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <SoftButton variant="text" color="secondary" onClick={() => setViewPlayer(null)}>Close</SoftButton>
              <SoftButton variant="gradient" color="primary" startIcon={<Icon>edit</Icon>}
                onClick={() => { setViewPlayer(null); openEdit(viewPlayer); }}>
                Edit Player
              </SoftButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </DashboardLayout>
  );
}

export default AcademyPlayers;
